import { fontCssForLabel, googleFontsHref, GOOGLE_FONTS } from "@/lib/google-fonts";

export const DEFAULT_BRAND = {
  enabled: false,
  mode: "cobrand",
  productName: "AivoryEMS",
  logoUrl: "",
  faviconUrl: "",
  colors: {
    primary: "#FF5608",
    primaryHover: "#E64D00",
    accent: "#60A5FA",
    background: "#09090B",
    surface: "#121214",
    elev: "#1A1A1D",
  },
  fonts: { heading: "Outfit", body: "DM Sans", googleFontsUrl: "" },
  showPoweredBy: true,
};

const FAVICON_ID = "aivory-tenant-favicon";
export const DEFAULT_FAVICON_HREF = "/aivory-mark.svg";
const FONT_LINK_ID = "aivory-tenant-google-fonts";
const CUSTOM_CSS_ID = "aivory-tenant-custom-css";
const UI_THEME_KEY = "aivoryems.ui.theme";
const THEME_SURFACE_VAR_PREFIXES = ["--bg-", "--text-", "--border-"];
const BRAND_ROOT_VARS = [
  "--font-heading",
  "--font-body",
  "--primary",
  "--primary-hover",
  "--bg-base",
  "--bg-surface",
  "--bg-elev",
  "--text-primary",
  "--text-secondary",
  "--text-tertiary",
  "--border-default",
  "--border-subtle",
  "--border-hover",
  "--bg-base-alpha70",
  "--bg-base-alpha80",
  "--bg-base-alpha95",
  "--bg-surface-alpha80",
  "--on-primary",
];
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const UPLOADS_PATH = /^\/uploads\/[a-zA-Z0-9][a-zA-Z0-9_./-]*$/;
const CLOUDINARY_URL = /^https:\/\/res\.cloudinary\.com\/[a-zA-Z0-9_./-]+$/;
const GOOGLE_FONTS_ORIGIN = "https://fonts.googleapis.com/";
const CUSTOM_CSS_BLOCK =
  /<\/style|<script|javascript:|expression\s*\(|-moz-binding|behavior\s*:|@import|@charset|@namespace|url\s*\(\s*['"]?\s*(?:javascript|data):/gi;

const ALLOWED_FONT_LABELS = new Set(GOOGLE_FONTS.map((f) => f.label));

function stripControlChars(value) {
  return String(value || "").replace(/[\x00-\x1f\x7f]/g, "");
}

export function sanitizeBrandUrl(url) {
  const u = stripControlChars(String(url || "").trim());
  if (!u) return "";
  const low = u.toLowerCase();
  if (low.startsWith("javascript:") || low.startsWith("data:") || low.startsWith("vbscript:")) return "";
  if (u.startsWith("//")) return "";
  if (UPLOADS_PATH.test(u)) return u;
  if (CLOUDINARY_URL.test(u)) return u;
  try {
    if (low.startsWith("http://") || low.startsWith("https://")) {
      const parsed = new URL(u);
      if (parsed.username || parsed.password) return "";
      if (CLOUDINARY_URL.test(u)) return u;
      const path = parsed.pathname || "";
      if (UPLOADS_PATH.test(path)) return path;
    }
  } catch {
    return "";
  }
  return "";
}

export function sanitizeHexColor(value, fallback) {
  const raw = String(value || "").trim();
  return HEX_COLOR.test(raw) ? raw.toUpperCase() : fallback;
}

export function optionalHexColor(value) {
  const raw = String(value || "").trim();
  return HEX_COLOR.test(raw) ? raw.toUpperCase() : "";
}

export function lightenHex(hex, factor = 0.12) {
  const raw = String(hex || "").trim();
  if (!HEX_COLOR.test(raw)) return DEFAULT_BRAND.colors.surface;
  const r = parseInt(raw.slice(1, 3), 16);
  const g = parseInt(raw.slice(3, 5), 16);
  const b = parseInt(raw.slice(5, 7), 16);
  const lr = Math.max(0, Math.min(255, Math.round(r + (255 - r) * factor)));
  const lg = Math.max(0, Math.min(255, Math.round(g + (255 - g) * factor)));
  const lb = Math.max(0, Math.min(255, Math.round(b + (255 - b) * factor)));
  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`.toUpperCase();
}

export function darkenHex(hex, factor = 0.88) {
  const raw = String(hex || "").trim();
  if (!HEX_COLOR.test(raw)) return DEFAULT_BRAND.colors.primaryHover;
  const r = Math.max(0, Math.min(255, Math.round(parseInt(raw.slice(1, 3), 16) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(raw.slice(3, 5), 16) * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(raw.slice(5, 7), 16) * factor)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`.toUpperCase();
}

/** WCAG 2.x relative luminance for sRGB hex (0 = black, 1 = white). */
export function relativeLuminance(hex) {
  const raw = String(hex || "").trim();
  if (!HEX_COLOR.test(raw)) return 0;
  const linear = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const r = parseInt(raw.slice(1, 3), 16);
  const g = parseInt(raw.slice(3, 5), 16);
  const b = parseInt(raw.slice(5, 7), 16);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** True when console background reads as light (use dark text tokens). */
export function isLightBackground(hex) {
  return relativeLuminance(hex) > 0.5;
}

function hexRgb(hex) {
  const raw = String(hex || "").trim();
  if (!HEX_COLOR.test(raw)) return null;
  return {
    r: parseInt(raw.slice(1, 3), 16),
    g: parseInt(raw.slice(3, 5), 16),
    b: parseInt(raw.slice(5, 7), 16),
  };
}

function rgbaFromHex(hex, alpha) {
  const rgb = hexRgb(hex);
  if (!rgb) return "";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/** Tokens aligned with :root / .light in index.css */
const DARK_CONTRAST_VARS = {
  "--text-primary": "#FAFAFA",
  "--text-secondary": "#A1A1AA",
  "--text-tertiary": "#71717A",
  "--border-default": "#27272A",
  "--border-subtle": "rgba(255, 255, 255, 0.06)",
  "--border-hover": "#3a3a3f",
};

const LIGHT_CONTRAST_VARS = {
  "--text-primary": "#09090B",
  "--text-secondary": "#52525B",
  "--text-tertiary": "#71717A",
  "--border-default": "#D4D4D8",
  "--border-subtle": "rgba(0, 0, 0, 0.06)",
  "--border-hover": "#A1A1AA",
};

/**
 * Text/border CSS vars for readable UI on a custom console background.
 * Example: contrastThemeVars("#FFFFFF") → dark text (--text-primary #09090B, …).
 */
export function contrastThemeVars(backgroundHex) {
  const bg = String(backgroundHex || "").trim();
  if (!HEX_COLOR.test(bg)) return {};

  const light = isLightBackground(bg);
  const base = light ? LIGHT_CONTRAST_VARS : DARK_CONTRAST_VARS;
  const surface = optionalHexColor(lightenHex(bg)) || lightenHex(bg);

  return {
    ...base,
    "--bg-base-alpha70": rgbaFromHex(bg, 0.7),
    "--bg-base-alpha80": rgbaFromHex(bg, 0.8),
    "--bg-base-alpha95": rgbaFromHex(bg, 0.95),
    "--bg-surface-alpha80": rgbaFromHex(surface, 0.8),
    "--on-primary": light ? "#FFFFFF" : "#09090B",
  };
}

export function sanitizeProductName(value, fallback = "Workspace") {
  return stripControlChars(String(value || fallback))
    .replace(/[<>]/g, "")
    .slice(0, 80) || fallback;
}

export function sanitizeFontLabel(value, fallback) {
  const label = stripControlChars(String(value || "").trim());
  return ALLOWED_FONT_LABELS.has(label) ? label : fallback;
}

export function sanitizeCustomCss(css) {
  let s = stripControlChars(String(css || "").trim()).slice(0, 8000);
  if (!s) return "";
  s = s.replace(CUSTOM_CSS_BLOCK, "");
  s = s.replace(/[<>]/g, "");
  return s.trim();
}

export function safeGoogleFontsHref(heading, body) {
  const labels = [
    sanitizeFontLabel(heading, "Outfit"),
    sanitizeFontLabel(body, "DM Sans"),
  ];
  const href = googleFontsHref(labels);
  if (!href || !href.startsWith(GOOGLE_FONTS_ORIGIN)) return "";
  return href;
}

export function normalizePublicBrand(raw) {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_BRAND };
  const colors = raw.colors && typeof raw.colors === "object" ? raw.colors : DEFAULT_BRAND.colors;
  const fonts = raw.fonts && typeof raw.fonts === "object" ? raw.fonts : DEFAULT_BRAND.fonts;
  const heading = sanitizeFontLabel(fonts.heading, "Outfit");
  const body = sanitizeFontLabel(fonts.body, "DM Sans");
  return {
    enabled: Boolean(raw.enabled),
    mode: raw.mode === "cobrand" ? "cobrand" : "full",
    productName: sanitizeProductName(raw.productName, DEFAULT_BRAND.productName),
    logoUrl: sanitizeBrandUrl(raw.logoUrl),
    faviconUrl: sanitizeBrandUrl(raw.faviconUrl),
    colors: (() => {
      const primary = sanitizeHexColor(colors.primary, DEFAULT_BRAND.colors.primary);
      const background = optionalHexColor(colors.background);
      const surface = optionalHexColor(colors.surface) || (background ? lightenHex(background) : "");
      const elev =
        optionalHexColor(colors.elev) ||
        (surface || background ? lightenHex(surface || background, 0.08) : "");
      return {
        primary,
        primaryHover: sanitizeHexColor(colors.primaryHover, darkenHex(primary)),
        accent: sanitizeHexColor(colors.accent, DEFAULT_BRAND.colors.accent),
        background,
        surface,
        elev,
      };
    })(),
    fonts: {
      heading,
      body,
      googleFontsUrl: safeGoogleFontsHref(heading, body),
      headingCss: fontCssForLabel(heading),
      bodyCss: fontCssForLabel(body),
    },
    showPoweredBy: raw.showPoweredBy !== false,
    customCss: sanitizeCustomCss(raw.customCss),
  };
}

function isSurfaceThemeVar(key) {
  return THEME_SURFACE_VAR_PREFIXES.some((prefix) => String(key).startsWith(prefix));
}

export function readStoredUiTheme() {
  try {
    const stored = localStorage.getItem(UI_THEME_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
}

function filterVarsForUiTheme(vars, uiTheme) {
  if (uiTheme !== "light") return vars;
  return Object.fromEntries(Object.entries(vars).filter(([key]) => !isSurfaceThemeVar(key)));
}

export function brandCssVars(brand, options = {}) {
  const { respectUiTheme = false, uiTheme } = options;
  const b = normalizePublicBrand(brand);
  const vars = {
    "--font-heading": b.fonts.headingCss,
    "--font-body": b.fonts.bodyCss,
  };
  // Only override theme tokens when whitelabel is active — disabled brand must not
  // emit circular vars (e.g. --primary: var(--primary)) that break auth buttons.
  if (b.enabled) {
    vars["--primary"] = b.colors.primary;
    vars["--primary-hover"] = b.colors.primaryHover;
  }
  if (b.enabled && b.colors.background) {
    vars["--bg-base"] = b.colors.background;
    vars["--bg-surface"] = b.colors.surface || lightenHex(b.colors.background);
    vars["--bg-elev"] = b.colors.elev || lightenHex(vars["--bg-surface"], 0.08);
    Object.assign(vars, contrastThemeVars(b.colors.background));
  }
  // Custom console background always wins over the global light/dark UI theme toggle.
  if (respectUiTheme && !(b.enabled && b.colors.background)) {
    return filterVarsForUiTheme(vars, uiTheme ?? readStoredUiTheme());
  }
  return vars;
}

function faviconCacheKey(meta, brand) {
  if (meta?.tenantId) return String(meta.tenantId);
  if (meta?.faviconKey) return String(meta.faviconKey);
  if (brand?.enabled && brand?.faviconUrl) return `${brand.productName || "tenant"}:${brand.faviconUrl}`;
  return "aivory-default";
}

function setDocumentFavicon(href, cacheKey = "aivory-default") {
  if (typeof document === "undefined") return;
  const sanitized = href ? sanitizeBrandUrl(href) : "";
  const base = sanitized || DEFAULT_FAVICON_HREF;
  const busted = `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(cacheKey)}`;
  let link = document.getElementById(FAVICON_ID);
  if (!link) {
    link = document.createElement("link");
    link.id = FAVICON_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (base.endsWith(".svg")) {
    link.type = "image/svg+xml";
  } else {
    link.removeAttribute("type");
  }
  link.setAttribute("href", busted);
}

function applyBrandRootVars(brand) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const b = normalizePublicBrand(brand);
  const vars = brandCssVars(b);
  for (const key of BRAND_ROOT_VARS) {
    root.style.removeProperty(key);
  }
  if (!b.enabled) return;
  for (const [key, value] of Object.entries(vars)) {
    if (!value) continue;
    root.style.setProperty(key, value);
  }
}

export function applyBrandDocumentEffects(brand, meta = {}, options = {}) {
  const b = normalizePublicBrand(brand);
  applyBrandRootVars(b);
  const titleBase = b.enabled ? b.productName : "AivoryEMS";
  if (typeof document !== "undefined" && !document.title.includes("·")) {
    document.title = titleBase;
  }

  const faviconHref = b.enabled ? sanitizeBrandUrl(b.faviconUrl) : "";
  setDocumentFavicon(faviconHref, faviconCacheKey(meta, b));

  const href = safeGoogleFontsHref(b.fonts.heading, b.fonts.body);
  let fontLink = document.getElementById(FONT_LINK_ID);
  if (href) {
    if (!fontLink) {
      fontLink = document.createElement("link");
      fontLink.id = FONT_LINK_ID;
      fontLink.rel = "stylesheet";
      document.head.appendChild(fontLink);
    }
    fontLink.href = href;
  } else if (fontLink) {
    fontLink.remove();
  }

  const css = sanitizeCustomCss(b.customCss);
  let styleEl = document.getElementById(CUSTOM_CSS_ID);
  if (css) {
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = CUSTOM_CSS_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
  } else if (styleEl) {
    styleEl.remove();
  }
}

/** Remove global whitelabel injections (fonts, custom CSS, tenant favicon). */
export function clearTenantDocumentEffects() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const key of BRAND_ROOT_VARS) {
    root.style.removeProperty(key);
  }
  document.getElementById(FONT_LINK_ID)?.remove();
  document.getElementById(CUSTOM_CSS_ID)?.remove();
  setDocumentFavicon("", "aivory-default");
}

export const LOGIN_DOCUMENT_TITLE = "Sign in · AivoryEMS";
export const SIGNUP_DOCUMENT_TITLE = "Start trial · AivoryEMS";
export const ONBOARDING_DOCUMENT_TITLE = "Set up workspace · AivoryEMS";

/** Reset document head after admin/platform — safe landing state for /login. */
export function resetLoginDocumentBranding(title = LOGIN_DOCUMENT_TITLE) {
  if (typeof document === "undefined") return;
  clearTenantDocumentEffects();
  applyBrandDocumentEffects({ ...DEFAULT_BRAND, enabled: false, faviconUrl: "", logoUrl: "" }, { faviconKey: "login" });
  document.title = title;
  const root = document.documentElement;
  root.classList.add("light");
  root.classList.remove("dark");
}

/** Platform ops console — never inherit a customer workspace favicon/title. */
export function resetPlatformDocumentBranding(title = "Aivory EMS · Platform") {
  if (typeof document === "undefined") return;
  clearTenantDocumentEffects();
  applyBrandDocumentEffects({ ...DEFAULT_BRAND, enabled: false, faviconUrl: "", logoUrl: "" }, { faviconKey: "platform" });
  document.title = title;
  for (const id of [FONT_LINK_ID, CUSTOM_CSS_ID]) {
    document.getElementById(id)?.remove();
  }
  const root = document.documentElement;
  root.classList.add("dark");
  root.classList.remove("light");
}
